import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { AvailabilityDto, AvailabilityStatus } from 'api/model';
import { useUserGetEmployeeAvailabilityWeekOverview } from 'api/user/user';
import useTranslations from 'contexts/basicData/useTranslations';
import { getDayName } from 'utils/date-helpers';
import AbsenceReasonIcon from './AbsenceReasonIcon';
import AvailableDaysPill from './AvailableDaysPill';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 6px;
`;

const DayButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 3px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
  text-align: center;
`;

const DayLabel = styled.span`
  font-size: 0.8em;
  white-space: nowrap;
`;

const DayBox = styled.span<{ $color?: string; $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 26px;
  padding: 2px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 3px;
  background: ${({ $color }) => $color ?? 'transparent'};
  font-size: 0.85em;
  font-variant-numeric: tabular-nums;
  ${({ $selected, theme }) =>
    $selected &&
    css`
      outline: 2px solid ${theme.colors.background.selection};
      outline-offset: 1px;
    `}
`;

const History = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  font-size: 0.85em;
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 6px;
`;

const Bar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const BarFill = styled.div<{ $days: number; $current: boolean }>`
  width: 14px;
  height: ${({ $days }) => Math.max($days * 4, 1)}px;
  border-radius: 2px;
  background: ${({ $current, theme }) =>
    $current ? '#6abd64' : theme.colors.background.tertiary};
`;

// Måndagen i veckan, lokal midnatt — samma vecka ger samma frågenyckel när man bläddrar dag för dag.
const getMonday = (date: Date) => {
  const monday = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  return monday;
};

const parseDateString = (dateString: string) => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatAverage = (value: number) =>
  value.toLocaleString('sv-SE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

interface Props {
  userId: number;
  /** Dagen som visas i modalen; blocket visar dess vecka och ramar in dagen. */
  date: Date;
  onSelectDate(date: Date): void;
}

/**
 * Veckoblocket i tillgänglighetsmodalen för behovsanställda: hur många dagar (mån–sön) FT:n markerat
 * sig tillgänglig, dag för dag, och samma siffra för de närmast föregående veckorna.
 */
const WeekAvailabilityOverview: React.FC<Props> = ({
  userId,
  date,
  onSelectDate,
}) => {
  const {
    availabilityStatuses,
    availabilityStatusColorCodes,
    absenceReasons,
    absenceStatuses,
  } = useTranslations();

  const monday = useMemo(() => getMonday(date), [date]);
  const { data } = useUserGetEmployeeAvailabilityWeekOverview(
    { userId, date: monday },
    {
      query: {
        keepPreviousData: true,
        refetchOnWindowFocus: false,
      },
    }
  );

  if (!data) return null;

  const selectedDateString = `${date.getFullYear()}-${String(
    date.getMonth() + 1
  ).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const average =
    data.recentWeeks.length > 0
      ? data.recentWeeks.reduce((sum, w) => sum + w.availableDays, 0) /
        data.recentWeeks.length
      : 0;

  const renderDayContent = (day: AvailabilityDto) => {
    if (day.absenceReason !== undefined && day.absenceReason !== null) {
      return (
        <AbsenceReasonIcon
          absenceReason={day.absenceReason}
          absenceStatus={day.absenceStatus}
          absenceReasons={absenceReasons}
          absenceStatuses={absenceStatuses}
        />
      );
    }
    if (
      day.scheduleStatus === AvailabilityStatus.AVAILABLE &&
      day.startString &&
      day.endString
    ) {
      return `${day.startString}–${day.endString}`;
    }
    return '–';
  };

  return (
    <Container>
      <Header>
        <span>{`Vecka ${data.weekNumber}`}</span>
        <AvailableDaysPill days={data.availableDays} long />
      </Header>
      <DayGrid>
        {data.days.map((day) => {
          const dayDate = parseDateString(day.dateString);
          return (
            <DayButton
              key={day.dateString}
              onClick={() => onSelectDate(dayDate)}
              title={availabilityStatuses[day.scheduleStatus]}
              type="button"
            >
              <DayLabel>
                {`${getDayName(dayDate)} ${dayDate.getDate()}/${
                  dayDate.getMonth() + 1
                }`}
              </DayLabel>
              <DayBox
                $color={availabilityStatusColorCodes[day.scheduleStatus]}
                $selected={day.dateString === selectedDateString}
              >
                {renderDayContent(day)}
              </DayBox>
            </DayButton>
          );
        })}
      </DayGrid>
      {data.recentWeeks.length > 1 && (
        <History>
          <Bars>
            {data.recentWeeks.map((week) => (
              <Bar
                key={week.weekNumber}
                title={`Vecka ${week.weekNumber}: ${week.availableDays} av 7 dagar`}
              >
                <BarFill
                  $current={week.weekNumber === data.weekNumber}
                  $days={week.availableDays}
                />
                <span>{`v${week.weekNumber}`}</span>
              </Bar>
            ))}
          </Bars>
          <span>
            {`Snitt senaste ${data.recentWeeks.length} veckorna: ${formatAverage(
              average
            )} dagar/vecka`}
          </span>
        </History>
      )}
    </Container>
  );
};

export default WeekAvailabilityOverview;
